/**
 * ComingSoon — full-page placeholder for pages not yet built.
 *
 * Used for demo readiness: shows a polished "Em breve" state so judges
 * don't land on a broken or empty page.
 *
 * Props:
 *  - title:       page/feature name shown as the main heading
 *  - description: optional one-liner about what's coming
 */

import { RocketLaunch } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";

export interface ComingSoonProps {
  /** Page/feature name — shown as the main heading. */
  title: string;
  /** One-liner about what's coming (optional). */
  description?: string;
}

export function ComingSoon({ title, description }: ComingSoonProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 py-16 text-center animate-fade-in">
      {/* Icon container */}
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-xl bg-warning/15 border border-warning/30 flex items-center justify-center">
          <RocketLaunch
            weight="duotone"
            className="w-10 h-10 text-warning"
            aria-hidden="true"
          />
        </div>
        <div className="absolute inset-0 rounded-xl bg-warning/5 blur-xl -z-10" />
      </div>

      {/* Badge */}
      <Badge
        variant="outline"
        className="mb-4 bg-warning/15 text-warning border-warning/30 px-3 py-1 text-xs font-semibold uppercase tracking-wider"
      >
        Em breve
      </Badge>

      {/* Heading */}
      <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-3">{title}</h1>

      {/* Description */}
      {description && (
        <p className="text-base text-muted-foreground max-w-sm mb-8">{description}</p>
      )}

      {/* Subtle note */}
      <p className="text-sm text-muted-foreground/60 italic">
        Avisamos quando estiver pronto.
      </p>
    </div>
  );
}
